/**
 * 반영하지 않은 셀 표식 — 연동 표식과 같은 자리·모양의 직각 삼각형.
 *
 * 값이 들어가지 않은 셀임을 알린다. 연동 표식과 형태를 맞춘 이유는, 둘 다
 * "이 셀을 누르면 설명이 나온다"는 같은 약속이기 때문이다 — 색으로만 구분한다.
 *
 * 표식이 겹칠 때는 이것이 우선한다. 값이 비어 있다는 사실이 연동 여부나
 * 수정 여부보다 먼저 눈에 들어와야 한다.
 */
import { cn } from "@/lib/utils";

const MARKER_SIZE = 9; // 직각변 길이(px)

/**
 * 3개월 거부는 "표를 못 찾음"과 원인도 조치도 다르다. 같은 빨강으로 묶으면
 * 사용자가 IXD 쪽을 뒤지게 되므로 색부터 가른다.
 */
export type CellNoticeTone = "blocked" | "unsupported";

const TONE_FILL: Record<CellNoticeTone, string> = {
  blocked: "fill-red-500",
  unsupported: "fill-orange-500",
};

/**
 * 같은 tone이면 표식·셀 하단 사유 문구·셀 바탕이 한 계열이어야 한다. 셋을
 * 따로 적으면 tone이 늘거나 색이 바뀔 때 한 곳을 빠뜨려 서로 어긋난다 —
 * `Record`라 tone이 추가되면 컴파일 타임에 잡힌다.
 *
 * 시맨틱 토큰(`status-negative` = red/600)을 쓰지 않는 이유: 표식이 red-500이고
 * `unsupported`(주황)에 대응하는 토큰이 없어, 한쪽만 토큰으로 바꾸면 표식과
 * 문구 색이 지금 바로 어긋난다. 토큰화는 양쪽 색을 함께 정할 때 한다.
 */
export const TONE_TEXT: Record<CellNoticeTone, string> = {
  blocked: "text-red-500",
  unsupported: "text-orange-500",
};

/** 반영불가 셀 바탕. 사유를 읽기 전에 어느 칸이 막혔는지 먼저 보이게 한다 */
export const TONE_CELL_BG: Record<CellNoticeTone, string> = {
  blocked: "bg-red-50",
  unsupported: "bg-orange-50",
};

const CellNoticeMarker = ({ tone = "blocked" }: { tone?: CellNoticeTone }) => (
  <svg
    width={MARKER_SIZE}
    height={MARKER_SIZE}
    viewBox={`0 0 ${MARKER_SIZE} ${MARKER_SIZE}`}
    className={cn(
      "pointer-events-none absolute right-0 top-0 z-[2]",
      TONE_FILL[tone],
    )}
    data-notice-marker
    aria-hidden
  >
    <title>
      {tone === "unsupported"
        ? "표가 이 기간 칸을 쓰지 않아 반영하지 않았습니다."
        : "반영하지 않은 셀입니다."}
    </title>
    <polygon points={`0,0 ${MARKER_SIZE},0 ${MARKER_SIZE},${MARKER_SIZE}`} />
  </svg>
);

export default CellNoticeMarker;
